import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getEngagementScores = createAsyncThunk(
    'memberHealth/getEngagementScores',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_HEALTH_SCORES, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Scores retrieved successfully',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch scores', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch scores' });
        }
    }
);

export const getMemberHealthStats = createAsyncThunk(
    'memberHealth/getMemberHealthStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_HEALTH_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Statistics retrieved',
                    stats: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch statistics', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch statistics' });
        }
    }
);

export const getAtRiskMembers = createAsyncThunk(
    'memberHealth/getAtRiskMembers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_HEALTH_AT_RISK, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'At-risk members retrieved',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch at-risk members', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch at-risk members' });
        }
    }
);

export const getAutomationRules = createAsyncThunk(
    'memberHealth/getAutomationRules',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_HEALTH_RULES, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automation rules retrieved',
                    rules: data || []
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch automation rules', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch automation rules' });
        }
    }
);

export const createAutomationRule = createAsyncThunk(
    'memberHealth/createAutomationRule',
    async (ruleData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.MEMBER_HEALTH_RULES, ruleData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Rule created successfully',
                    rule: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to create rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to create rule' });
        }
    }
);

export const updateAutomationRule = createAsyncThunk(
    'memberHealth/updateAutomationRule',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.put(API_ENDPOINTS.MEMBER_HEALTH_RULE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule updated successfully',
                    rule: responseData || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to update rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to update rule' });
        }
    }
);

export const deleteAutomationRule = createAsyncThunk(
    'memberHealth/deleteAutomationRule',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.delete(API_ENDPOINTS.MEMBER_HEALTH_RULE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule deleted successfully',
                    id: id
                };
            }
            return rejectWithValue({ message: message || 'Failed to delete rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to delete rule' });
        }
    }
);

export const toggleAutomationRule = createAsyncThunk(
    'memberHealth/toggleAutomationRule',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.MEMBER_HEALTH_RULE_TOGGLE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule toggled successfully',
                    id: id,
                    is_active: data?.is_active || false
                };
            }
            return rejectWithValue({ message: message || 'Failed to toggle rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to toggle rule' });
        }
    }
);

export const sendBulkSmsToAtRisk = createAsyncThunk(
    'memberHealth/sendBulkSmsToAtRisk',
    async ({ member_ids, message }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.MEMBER_HEALTH_BULK_SMS, {
                member_ids,
                message
            });
            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'Bulk SMS sent successfully',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: responseMessage || 'Failed to send bulk SMS', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to send bulk SMS' });
        }
    }
);

export const getAtRiskAlerts = createAsyncThunk(
    'memberHealth/getAtRiskAlerts',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_HEALTH_ALERTS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Alerts retrieved',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch alerts', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch alerts' });
        }
    }
);

export const resolveAtRiskAlert = createAsyncThunk(
    'memberHealth/resolveAtRiskAlert',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.MEMBER_HEALTH_ALERT_RESOLVE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Alert resolved successfully',
                    alert: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to resolve alert', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to resolve alert' });
        }
    }
);

export const getMemberHealthGroups = createAsyncThunk(
    'memberHealth/getMemberHealthGroups',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_HEALTH_GROUPS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Groups retrieved',
                    groups: data || []
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch groups', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch groups' });
        }
    }
);

export const calculateMemberScore = createAsyncThunk(
    'memberHealth/calculateMemberScore',
    async ({ member_id }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.MEMBER_HEALTH_CALCULATE_SCORE, {
                member_id
            });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Score calculated successfully',
                    score: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to calculate score', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to calculate score' });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    scores: [],
    scoresPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    stats: null,
    atRiskMembers: [],
    atRiskPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    rules: [],
    alerts: [],
    alertsPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    groups: [],
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const memberHealthSlice = createSlice({
    name: 'memberHealth',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearMemberHealthState: (state) => {
            state.scores = [];
            state.scoresPagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.stats = null;
            state.atRiskMembers = [];
            state.atRiskPagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.rules = [];
            state.alerts = [];
            state.alertsPagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.groups = [];
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.isSubmitting = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get scores
            .addCase(getEngagementScores.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getEngagementScores.fulfilled, (state, action) => {
                state.isLoading = false;
                state.scores = action.payload.data?.data || [];
                state.scoresPagination = action.payload.data?.meta || initialState.scoresPagination;
                state.apiMessage = action.payload.message;
                state.successMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getEngagementScores.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch scores';
                state.apiMessage = action.payload?.message || 'Failed to fetch scores';
                state.scores = [];
            })

            // Get stats
            .addCase(getMemberHealthStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getMemberHealthStats.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stats = action.payload.stats || null;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getMemberHealthStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch statistics';
                state.stats = null;
            })

            // Get at-risk members
            .addCase(getAtRiskMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getAtRiskMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.atRiskMembers = action.payload.data?.data || [];
                state.atRiskPagination = action.payload.data?.meta || initialState.atRiskPagination;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getAtRiskMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch at-risk members';
                state.apiMessage = action.payload?.message || 'Failed to fetch at-risk members';
                state.atRiskMembers = [];
            })

            // Get rules
            .addCase(getAutomationRules.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getAutomationRules.fulfilled, (state, action) => {
                state.isLoading = false;
                state.rules = action.payload.rules || [];
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getAutomationRules.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch automation rules';
                state.apiMessage = action.payload?.message || 'Failed to fetch automation rules';
                state.rules = [];
            })

            // Create rule
            .addCase(createAutomationRule.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(createAutomationRule.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.rule) {
                    state.rules = [action.payload.rule, ...state.rules];
                }
            })
            .addCase(createAutomationRule.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to create rule';
                state.apiMessage = action.payload?.message || 'Failed to create rule';
            })

            // Update rule
            .addCase(updateAutomationRule.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(updateAutomationRule.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.rule) {
                    state.rules = state.rules.map(r =>
                        r.id === action.payload.rule.id ? action.payload.rule : r
                    );
                }
            })
            .addCase(updateAutomationRule.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to update rule';
                state.apiMessage = action.payload?.message || 'Failed to update rule';
            })

            // Delete rule
            .addCase(deleteAutomationRule.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(deleteAutomationRule.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.id) {
                    state.rules = state.rules.filter(r => r.id !== action.payload.id);
                }
            })
            .addCase(deleteAutomationRule.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to delete rule';
                state.apiMessage = action.payload?.message || 'Failed to delete rule';
            })

            // Toggle rule
            .addCase(toggleAutomationRule.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(toggleAutomationRule.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.id) {
                    state.rules = state.rules.map(r =>
                        r.id === action.payload.id ? { ...r, is_active: action.payload.is_active } : r
                    );
                }
            })
            .addCase(toggleAutomationRule.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle rule';
                state.apiMessage = action.payload?.message || 'Failed to toggle rule';
            })

            // Bulk SMS
            .addCase(sendBulkSmsToAtRisk.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(sendBulkSmsToAtRisk.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(sendBulkSmsToAtRisk.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to send bulk SMS';
                state.apiMessage = action.payload?.message || 'Failed to send bulk SMS';
            })

            // Get alerts
            .addCase(getAtRiskAlerts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getAtRiskAlerts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.alerts = action.payload.data?.data || [];
                state.alertsPagination = action.payload.data?.meta || initialState.alertsPagination;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getAtRiskAlerts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch alerts';
                state.apiMessage = action.payload?.message || 'Failed to fetch alerts';
                state.alerts = [];
            })

            // Resolve alert
            .addCase(resolveAtRiskAlert.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(resolveAtRiskAlert.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.alert) {
                    state.alerts = state.alerts.map(a =>
                        a.id === action.payload.alert.id ? action.payload.alert : a
                    );
                }
            })
            .addCase(resolveAtRiskAlert.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to resolve alert';
                state.apiMessage = action.payload?.message || 'Failed to resolve alert';
            })

            // Get groups
            .addCase(getMemberHealthGroups.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getMemberHealthGroups.fulfilled, (state, action) => {
                state.isLoading = false;
                state.groups = action.payload.groups || [];
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getMemberHealthGroups.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch groups';
                state.apiMessage = action.payload?.message || 'Failed to fetch groups';
                state.groups = [];
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearMemberHealthState,
} = memberHealthSlice.actions;

export const selectMemberHealthScores = (state) => state.memberHealth?.scores || [];
export const selectMemberHealthScoresPagination = (state) => state.memberHealth?.scoresPagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectMemberHealthStats = (state) => state.memberHealth?.stats || null;
export const selectAtRiskMembers = (state) => state.memberHealth?.atRiskMembers || [];
export const selectAtRiskPagination = (state) => state.memberHealth?.atRiskPagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectAutomationRules = (state) => state.memberHealth?.rules || [];
export const selectAtRiskAlerts = (state) => state.memberHealth?.alerts || [];
export const selectAtRiskAlertsPagination = (state) => state.memberHealth?.alertsPagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectMemberHealthGroups = (state) => state.memberHealth?.groups || [];
export const selectMemberHealthLoading = (state) => state.memberHealth?.isLoading || false;
export const selectMemberHealthSubmitting = (state) => state.memberHealth?.isSubmitting || false;
export const selectMemberHealthError = (state) => state.memberHealth?.error || null;
export const selectMemberHealthApiMessage = (state) => state.memberHealth?.apiMessage || null;

export default memberHealthSlice.reducer;